/**
 * Trader.API response contract for the new web app.
 *
 * Each schema describes the JSON the API sends today. Sources: Trader.API/Controllers, Core/Dtos, Core/Entities and
 * Binance.Net 12.13 (Data/load-data returns its IBinanceKline objects directly). The API uses the ASP.NET Core 8
 * System.Text.Json web defaults:
 * - names are camelCase, with the leading capitals lowered only: BuyClientID -> buyClientID, not buyClientId;
 * - every public property is written, null included, so a nullable field is present with null rather than missing;
 * - enums are numbers, C# decimal is a JSON number (it keeps its scale, 3.00, and up to 28 significant digits, more
 *   than a double holds), DateTime is ISO 8601. Mongo and Binance.Net hand back UTC values, written with a trailing Z;
 * - computed get-only properties are serialized too (CloseGroupResultDto.success, CancelOrdersResultDto.cancelled/failed).
 *
 * Rules used here:
 * - Objects are loose: a field the backend adds later passes through instead of failing the page.
 * - Ids, symbols and the numbers money is computed from are strict: a string where a number belongs fails.
 * - Binance order ids must be safe integers: JSON.parse rounds a larger id silently, and a rounded id would close or
 *   cancel the wrong order.
 * - The http layer turns an empty body into undefined; endpoints that can answer with one say so with emptyBody.
 * - Raw codes stay raw (futures tradeType, trade state, close status): turning them into meaning is domain code's job.
 *
 * Fields added by recent API commits are optional, so the app still loads against an API deployed before them.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------------------------------------------------
// Primitives

/** A MongoDB ObjectId as the API writes a [BsonRepresentation(ObjectId)] string id: 24 lowercase hex characters. */
export const mongoId = z.string().regex(/^[0-9a-f]{24}$/, 'expected a 24-character hex ObjectId');

/**
 * An order id (C# long), as a decimal string: OKX's ids pass 2^53, so the HTTP client reads them as strings (see
 * quoteUnsafeIntegers) while smaller ones still arrive as numbers. "0" means "no order" where a field says so.
 */
export const orderId = z.union([z.int().nonnegative(), z.string().regex(/^\d+$/, 'expected an order id')]).transform((id) => String(id));

/** The buyId that closes every open leg of a group or a line. */
export const WHOLE_GROUP = '0';

/** True for an order id as the close endpoints take it: digits only, a C# long (at most 19 digits). */
export function isOrderId(id: string): boolean {
  return /^\d{1,19}$/.test(id);
}

/** A C# decimal. Always a JSON number, never a string. */
export const decimal = z.number();

/** A C# DateTime. Normally UTC with a trailing Z; an offset, or no zone at all (DateTimeKind.Unspecified), is accepted. */
export const isoDateTime = z.iso.datetime({ offset: true, local: true });

/** A count (C# int). */
const count = z.int().nonnegative();

/** No body: Ok() without a value. The http layer turns the empty body into undefined. */
export const emptyBody = z.undefined();

const orEmptyBody = <T extends z.ZodType>(schema: T) => z.union([schema, emptyBody]);

// ---------------------------------------------------------------------------------------------------------------------
// Trades entity (Core/Entities/Trades.cs): a group trade's symbol legs and the bot's trades share it

/**
 * The fields of a Trades entity. The naming is spot-centric for both markets: buy* is the entry and sell* the exit,
 * so on a futures short buyPrice is the short entry and sellPrice the take-profit below it.
 */
const tradeFields = {
  /** The account the trade belongs to. Null on group legs: the API never sets it on embedded trades. */
  accountId: z.string().nullable(),
  symbol: z.string().min(1),
  /** The take-profit order, or after a manual close the close order. 0: no take-profit was placed. */
  sellOrderId: orderId,
  /** The entry order. The close endpoints take this as buyId. */
  buyOrderId: orderId,
  /** Entry price. On a group opened without a take-profit it is the hourly kline close at creation, not the fill. */
  buyPrice: decimal,
  /** Take-profit price. 0 when the group was opened without one. */
  sellPrice: decimal,
  /** Used by the bot only; 0 on group legs. */
  stopPrice: decimal,
  /** Quantity. 0 on a futures group opened without a take-profit (the entry's acknowledged quantity). */
  quantity: decimal,
  /** State enum: 0 New, 1 Active, 2 Even, 3 Closed, 4 NewEven. Group legs stay 1 after they close: use finished. */
  state: z.int(),
  /**
   * Money in the quote currency. For a finished trade the realized profit, rounded to 2 decimals. For an open group
   * leg the API recomputes it on every request from the last hourly kline close: (price / entry - 1) * group value,
   * or (entry / price - 1) * group value for a futures group whose tradeType is not 1.
   */
  profit: decimal,
  /** Set by the bot. Null on group legs: the group's createdOn is their open time. */
  openTime: isoDateTime.nullable(),
  buyClientID: z.string().nullable(),
  sellClientID: z.string().nullable(),
  /** True once the close fill was processed. The one field that says whether the trade is still open. */
  finished: z.boolean(),
  /** When the close fill was processed (group legs only). Added in API commit b7484d0. */
  closedOn: isoDateTime.nullable().optional(),
  /** Client order id of the latest manual spot market sell. Added in API commit 0d6da01. */
  closeClientID: z.string().nullable().optional(),
  /** What the take-profit filled before a manual close cancelled it, and its average price. Added in 0d6da01. */
  targetFilledQuantity: decimal.optional(),
  targetFilledPrice: decimal.optional(),
  /** Bot trades: null or 0 spot, 1 futures. Null on group legs. */
  botType: z.int().nullable(),
};

/**
 * Computed by the group lists only, never stored (backend item B14): the symbol's precisions, and for an open leg the
 * hourly close the server valued its profit with. Absent from an API from before B14, and on a leg whose symbol the
 * server has no data for.
 */
const groupLegExtras = {
  pricePrecision: count.optional(),
  quantityPrecision: count.optional(),
  lastPrice: decimal.optional(),
};

/** A symbol leg of a spot, futures or line-break group. Its id is null (embedded documents get none): key it by buyOrderId. */
export const groupTradeLeg = z.looseObject({
  id: z.string().nullable(),
  ...tradeFields,
  ...groupLegExtras,
});

/** A bot trade from the Trades collection (bot history). */
export const botTrade = z.looseObject({
  id: mongoId,
  ...tradeFields,
});

// ---------------------------------------------------------------------------------------------------------------------
// Groups (Core/Entities/GroupTrade.cs, FutureGroupTrade.cs, LineBreakTrade.cs)

const groupFields = {
  id: mongoId,
  accountId: z.string().nullable(),
  name: z.string(),
  isActive: z.boolean(),
  /** Take-profit in percent, as entered (0: opened without a take-profit). Not money: the legs' profit is. */
  profit: decimal,
  /** Value per symbol in the quote currency, as entered. */
  value: decimal,
  /** The symbols asked for. A symbol whose entry order failed is in here but has no leg in trades. */
  symbols: z.array(z.string()),
  createdOn: isoDateTime,
  trades: z.array(groupTradeLeg),
};

export const spotGroupTrade = z.looseObject(groupFields);

export const futuresGroupTrade = z.looseObject({
  ...groupFields,
  /**
   * Binance.Net PositionSide as a raw number: 1 Long, 0 Short, 2 Both (never a valid direction). Kept raw so a missing
   * or unexpected value is never read as long; src/domain decides. Beware: the server treats every value but 1 as
   * short when it places and closes orders, and a stored group missing the field reads back as 0 (short).
   */
  tradeType: z.number().nullish(),
});

export const lineTrade = z.looseObject({
  ...groupFields,
  /** BreakType: 0 Break, 1 ReTest. */
  type: z.int(),
});

// ---------------------------------------------------------------------------------------------------------------------
// Bot open orders (Core/Dtos/OrdersDto.cs)

/** An open take-profit order of a bot trade, enriched by the API. */
export const order = z.looseObject({
  /** The take-profit limit price. */
  price: decimal,
  /** Not a fill price: the API overwrites it with the bot trade's entry price. */
  averageFillPrice: decimal.nullable(),
  clientOrderId: z.string().nullable(),
  createTime: isoDateTime,
  symbol: z.string().min(1),
  /** Binance.Net enum name (on OKX too). "Sell" closes a long; "Buy", a short's take-profit, only with ?sides=all (futures). */
  side: z.string().nullable(),
  /** The exchange's order id: a decimal string, as OKX's pass 2^53 (see orderId). */
  id: orderId,
  originalClientOrderId: z.string().nullable(),
  quantity: decimal,
  quantityFilled: decimal,
  /** What is left of the order (an older API answered a futures order's full quantity here). */
  quantityRemaining: decimal,
  /** Binance.Net enum name: "New", "PartiallyFilled". */
  status: z.string().nullable(),
  /** Binance.Net enum name: "Limit", "LimitMaker". */
  type: z.string().nullable(),
  /** Percent: (current price / entry - 1) * 100, from a price the API fetches per order. Computed as a long. */
  profit: decimal.nullable(),
  /** Money: the unrealized profit in the quote currency. */
  profitValue: decimal.nullable(),
  /** Money: quantity * entry price. */
  amount: decimal.nullable(),
});

export const symbolOrders = z.looseObject({
  symbol: z.string().min(1),
  /** Sum of the orders' percents: not a meaningful figure. */
  profit: decimal.nullable(),
  profitValue: decimal.nullable(),
  /** Average entry price of the symbol's orders. */
  avgPrice: decimal.nullable(),
  count,
  orders: z.array(order),
});

export const ordersResult = z.looseObject({
  /** Sum of every order's percent: not a meaningful figure. Use profitValue. */
  profit: decimal.nullable(),
  profitValue: decimal.nullable(),
  count,
  /** Sorted by the symbol's total amount, largest first. */
  symbolOrders: z.array(symbolOrders),
  allOrders: z.array(order),
});

// ---------------------------------------------------------------------------------------------------------------------
// Bot history (Core/Dtos/TradeSummaryDto.cs)

export const tradeSummary = z.looseObject({
  profit: decimal,
  count,
  /** Only trades with a non-zero profit whose openTime is in the range. */
  trades: z.array(botTrade),
});

// ---------------------------------------------------------------------------------------------------------------------
// Close and cancel results (Core/Dtos/CloseResultDto.cs)

export const CLOSE_TRADE_STATUSES = ['closed', 'alreadyClosed', 'failed', 'unknown'] as const;

/**
 * The statuses the API sends today. closeTradeResult keeps status as a raw string so that a status added later does
 * not hide a close outcome: check it with closeTradeStatus.safeParse and treat anything else as unknown.
 */
export const closeTradeStatus = z.enum(CLOSE_TRADE_STATUSES);

export const closeTradeResult = z.looseObject({
  symbol: z.string().min(1),
  /** The leg's buyOrderId. */
  buyOrderId: orderId,
  /** True for closed and alreadyClosed. */
  success: z.boolean(),
  /** closed | alreadyClosed | failed | unknown. unknown: an order may have reached Binance, check there before retrying. */
  status: z.string().nullable(),
  error: z.string().nullable(),
});

export const closeGroupResult = z.looseObject({
  /** The groupId of the request, echoed. */
  groupId: z.string(),
  /** Every trade succeeded. True when trades is empty (nothing matched buyId): check trades.length first. */
  success: z.boolean(),
  trades: z.array(closeTradeResult),
});

/** A symbol and why it did not open, or why it has no take-profit (Trader.API/Core/Dtos/AddGroupResultDto.cs). */
export const symbolProblem = z.looseObject({
  symbol: z.string().nullable(),
  error: z.string().nullable(),
});

/** What opening a group did, symbol by symbol. */
export const addGroupResult = z.looseObject({
  /** The saved group; null when no symbol opened, and then no group was saved. */
  groupId: z.string().nullable(),
  /** The symbols asked for, trimmed, upper-cased and without repeats. */
  requested: z.array(z.string()),
  /** Entry went through: in the group. */
  opened: z.array(z.string()),
  /** Binance did not open them: not in the group, nothing to close. */
  notOpened: z.array(symbolProblem),
  /** The entry may or may not have gone through: a position may be open outside the group. */
  unconfirmed: z.array(symbolProblem),
  /** In the group without a take-profit order. */
  noTakeProfit: z.array(symbolProblem),
  /** Problems that did not change what was ordered, such as a save that failed after the group was created. */
  warnings: z.array(z.string()),
  /** Nothing in any of the problem lists or warnings. */
  success: z.boolean(),
});

export const cancelOrderResult = z.looseObject({
  symbol: z.string().min(1),
  orderId: orderId,
  clientOrderId: z.string().nullable(),
  success: z.boolean(),
  error: z.string().nullable(),
});

export const cancelOrdersResult = z.looseObject({
  symbol: z.string().min(1),
  cancelled: count,
  failed: count,
  orders: z.array(cancelOrderResult),
});

// ---------------------------------------------------------------------------------------------------------------------
// Symbols (Core/Entities/Symbol.cs, FutureSymbol.cs, SymbolGroup.cs, FutureSymbolGroup.cs)

const symbolFields = {
  id: mongoId,
  name: z.string().min(1),
  /** Decimals of the price tick size. */
  pricePrecision: count,
  /** Decimals of the lot step size. */
  quantityPrecision: count,
  /** Last hourly kline close (get-symbols), or the value stored by the last sync (get-symbols-basic). */
  price: decimal,
  /** Quote volume of the last 24 hourly klines. */
  volume: decimal,
};

export const spotSymbol = z.looseObject({
  ...symbolFields,
  /** Percent below the highest hourly close of the last 24 hours (0 or negative), not the 24-hour change. */
  change: decimal,
});

/** Futures symbols have no change field. */
export const futuresSymbol = z.looseObject(symbolFields);

export const symbolGroup = z.looseObject({
  id: mongoId,
  accountId: z.string().nullable(),
  name: z.string(),
  symbols: z.array(z.string()),
});

// ---------------------------------------------------------------------------------------------------------------------
// Charts

/** Binance.Net IBinanceKline, serialized through its interface. */
export const kline = z.looseObject({
  openTime: isoDateTime,
  openPrice: decimal,
  highPrice: decimal,
  lowPrice: decimal,
  closePrice: decimal,
  volume: decimal,
  /** The last kline is the current, still open one: its closeTime is in the future. */
  closeTime: isoDateTime,
  quoteVolume: decimal,
  tradeCount: count,
  takerBuyBaseVolume: decimal.optional(),
  takerBuyQuoteVolume: decimal.optional(),
});

/** Core/Charts/SymbolLineResult.cs. One entry per line found, so a symbol can appear more than once. */
export const lineChart = z.looseObject({
  symbol: z.string().min(1),
  /** A PNG data URL (data:image/png;base64,...), about 1800x1200, used as an <img> src. */
  image: z.string().min(1),
});

/**
 * One entry of a ReceivePrice (spot) or ReceiveFuturesPrice (usdm) batch: Core/Dtos/PriceSnapshotDto.cs PriceTickDto,
 * sent by Subscribers/SymbolPriceStateStore.cs.
 */
export const priceTick = z.looseObject({
  symbol: z.string().min(1),
  lastPrice: decimal,
  /** When Binance produced this price, Unix ms (Binance's clock). Absent from an older API. */
  eventTime: z.int().optional(),
});

// ---------------------------------------------------------------------------------------------------------------------
// Capabilities and price snapshot (backend items B4 and B7)

/** GET /Features: what the API supports, so the web app turns features on as the server gains them. */
export const featuresResponse = z.looseObject({
  /** Close endpoints answer per-trade results. */
  closeResults: z.boolean(),
  /** Add-group endpoints check input first and answer per-symbol results. */
  addResults: z.boolean(),
  /** GET /Prices/snapshot exists (spot). */
  priceSnapshot: z.boolean(),
  /**
   * Futures prices are streamed (hub event ReceiveFuturesPrice, for clients that connect with ?feeds=usdm) and
   * GET /Prices/snapshot?market=usdm answers them; until then futures screens show spot prices as a reference.
   */
  futuresPriceFeed: z.boolean(),
  /** GET get-orders?sides=all also lists buy orders (futures shorts' take-profits). Absent before phase 5. */
  botOrderSides: z.boolean().optional(),
  /** The bot's cancel routes answer POST. Absent before phase 5: cancelling is then left to the old app. */
  botPostCancel: z.boolean().optional(),
  /** GET /FutureSymbols/get-future-symbols-basic exists (stored symbols, no Binance call). */
  futuresSymbolsBasic: z.boolean().optional(),
  /** GET /P2P/prices answers the best Binance P2P prices of USDT in AED and JOD. */
  p2pPrices: z.boolean().optional(),
  /** /Accounts lists, adds, edits, removes and switches the user's exchange accounts. */
  accounts: z.boolean().optional(),
  /** OKX prices: the hub's ?feeds=okx (ReceiveOkxPrice, ReceiveOkxSwapPrice) and the okx-spot and okx-swap snapshots. */
  okxPrices: z.boolean().optional(),
});

/** Core/Dtos/PriceSnapshotDto.cs PriceSnapshotItemDto. */
export const priceSnapshotItem = z.looseObject({
  symbol: z.string().min(1),
  lastPrice: decimal,
  /** When the API received it from Binance, UTC ISO time (server clock). */
  receivedAt: z.string(),
  /**
   * When Binance produced it, Unix ms (Binance's clock); the same value as the hub tick's. Absent from an older API.
   * The newest one in a snapshot tells whether the server's own stream is current.
   */
  eventTime: z.int().optional(),
});

export const priceSnapshotResponse = z.array(priceSnapshotItem);

// ---------------------------------------------------------------------------------------------------------------------
// Accounts (AccountsController)

export const EXCHANGES = ['binance', 'okx'] as const;

/** An exchange account of the signed-in user, as listed: never its keys. */
export const exchangeAccount = z.looseObject({
  id: mongoId,
  name: z.string(),
  /** "binance" or "okx"; kept a string so an exchange added later does not break the list. */
  exchange: z.string(),
  /** OKX demo trading (no real money). */
  isDemo: z.boolean(),
  /** OKX swaps: cross or isolated margin. */
  marginMode: z.string().nullable(),
  /** The account the request's token is for. */
  current: z.boolean(),
});

export const accountsResponse = z.array(exchangeAccount);

/** Adding (keys required) or editing (empty keys keep the current ones) an account. */
export const accountRequest = z.object({
  name: z.string().min(1),
  exchange: z.enum(EXCHANGES),
  key: z.string(),
  secret: z.string(),
  passphrase: z.string().optional(),
  isDemo: z.boolean(),
  marginMode: z.enum(['cross', 'isolated']).optional(),
});

// ---------------------------------------------------------------------------------------------------------------------
// P2P (P2PController)

export const p2pOffer = z.looseObject({
  price: decimal,
  /** The smallest and largest order the advertiser takes, in the fiat currency (0 when Binance does not say). */
  minAmount: decimal,
  maxAmount: decimal,
  advertiser: z.string().nullable(),
});

/** The best current Binance P2P offers: AED per USDT to buy it, JOD per USDT to sell it. */
export const p2pPricesResponse = z.looseObject({
  aedUsdtBuyPrice: decimal,
  jodUsdtSellPrice: decimal,
  aedOffers: z.array(p2pOffer),
  jodOffers: z.array(p2pOffer),
  fetchedAt: isoDateTime,
});

/**
 * Buy USDT with AED, sell it for JOD, bring the JOD back to AED at the transfer rate (JOD per AED). Start from an AED
 * amount (amount) or a JOD one (jodAmount).
 */
export const p2pCalculateRequest = z.object({
  aedUsdtBuyPrice: z.number().positive(),
  jodUsdtSellPrice: z.number().positive(),
  transferRate: z.number().positive(),
  amount: z.number().positive().optional(),
  jodAmount: z.number().positive().optional(),
});

/** Profit in AED, and what the trip moves: from an AED amount, the JOD it gives; from a JOD one, the AED it costs. */
export const p2pCalculateResponse = z.looseObject({
  profit: decimal.nullable(),
  usdt: decimal.nullable(),
  jod: decimal.nullable().optional(),
  aed: decimal.nullable().optional(),
});

// ---------------------------------------------------------------------------------------------------------------------
// Errors

/**
 * application/problem+json. A close that waited 30 s for another one gives a 409 whose detail is safe to show (nothing
 * was sent); other unhandled exceptions give a 500 without detail; model validation gives a 400 with errors keyed by C#
 * property name.
 */
export const problemDetails = z.looseObject({
  type: z.string().nullish(),
  title: z.string().nullish(),
  status: z.int().nullish(),
  detail: z.string().nullish(),
  traceId: z.string().nullish(),
  errors: z.record(z.string(), z.array(z.string())).nullish(),
});

/**
 * A failed response's body: problem details, a text/plain message (BadRequest("..."), Unauthorized("Invalid Login");
 * a JSON string instead when the request sends Accept: application/json), or nothing (401 from the JWT check,
 * BadRequest(null), 404).
 */
export const apiErrorBody = z.union([problemDetails, z.string(), emptyBody]);

// ---------------------------------------------------------------------------------------------------------------------
// Responses, one per endpoint (see ENDPOINTS)

// Spot groups
export const spotGroupTradesResponse = z.array(spotGroupTrade);
/** Empty body from an API deployed before the add-group fix of 2026-11-02. */
export const addSpotGroupTradeResponse = orEmptyBody(addGroupResult);
/** Empty body from an API deployed before commit 0d6da01. */
export const closeSpotGroupTradeResponse = orEmptyBody(closeGroupResult);

// Futures groups
export const futuresGroupTradesResponse = z.array(futuresGroupTrade);
export const addFuturesGroupTradeResponse = orEmptyBody(addGroupResult);
export const closeFuturesGroupTradeResponse = orEmptyBody(closeGroupResult);

// Line-break groups
export const lineTradesResponse = z.array(lineTrade);
export const addLineTradeResponse = orEmptyBody(addGroupResult);
export const closeLineTradeResponse = orEmptyBody(closeGroupResult);
export const lineChartsResponse = z.array(lineChart);

// Symbols and symbol groups
export const spotSymbolsResponse = z.array(spotSymbol);
export const spotSymbolsBasicResponse = z.array(spotSymbol);
export const updateSpotSymbolsResponse = emptyBody;
export const spotSymbolGroupsResponse = z.array(symbolGroup);
export const saveSpotSymbolGroupResponse = emptyBody;
export const deleteSpotSymbolGroupResponse = emptyBody;
export const futuresSymbolsResponse = z.array(futuresSymbol);
export const updateFuturesSymbolsResponse = emptyBody;
export const futuresSymbolGroupsResponse = z.array(symbolGroup);
export const saveFuturesSymbolGroupResponse = emptyBody;
export const deleteFuturesSymbolGroupResponse = emptyBody;

// Bot
/** Empty body when the account has no open order at all (on any symbol, or on the one asked for). */
export const spotOrdersResponse = orEmptyBody(ordersResult);
export const futuresOrdersResponse = orEmptyBody(ordersResult);
export const cancelSpotOrderResponse = emptyBody;
export const cancelFuturesOrderResponse = emptyBody;
/**
 * Before commit 0d6da01 the API answered the number of ALL open orders (not what it cancelled), or an empty body when
 * there were none. Treat a number as "sent, no per-order result".
 */
const legacyCancelAllCount = z.int().nonnegative();
export const cancelAllSpotOrdersResponse = z.union([cancelOrdersResult, legacyCancelAllCount, emptyBody]);
export const cancelAllFuturesOrdersResponse = z.union([cancelOrdersResult, legacyCancelAllCount, emptyBody]);
export const spotTradeHistoryResponse = tradeSummary;
export const futuresTradeHistoryResponse = tradeSummary;

// Charts
export const klinesResponse = z.array(kline);

// Auth: text/plain. The token text already carries its scheme; it goes back verbatim as the Authorization header.
export const loginResponse = z.string().regex(/^Bearer \S+$/, 'expected "Bearer <jwt>"');

// SignalR /priceHub, event ReceivePrice: one argument, a batch of every spot symbol that ticked (all quote assets).
export const receivePriceMessage = z.array(priceTick);

// SignalR /priceHub?feeds=usdm, event ReceiveFuturesPrice: one argument, a batch of every USD-M futures symbol that ticked.
export const receiveFuturesPriceMessage = z.array(priceTick);

// ---------------------------------------------------------------------------------------------------------------------
// Request bodies the old app sends (for the http layer; the server binds with the same web defaults)

/** Body of get-trades. The server adds 4 hours to from and takes the date (UTC+4 assumed), and ends to at 23:59:59. */
export const tradeHistoryRequest = z.object({
  from: isoDateTime,
  to: isoDateTime,
  symbol: z.string().min(1).optional(),
});

/** Body of add-group-trade and add-line-trade. profit is the take-profit in percent, 0 for none. */
export const groupTradeRequest = z.object({
  name: z.string().min(1),
  value: z.number().positive(),
  profit: z.number().nonnegative(),
  symbols: z.array(z.string().min(1)).min(1),
});

/** Body of add-future-group-trade. tradeType must be 1 (long) or 0 (short): anything else, or none, is a 400 before any order. */
export const futuresGroupTradeRequest = groupTradeRequest.extend({
  tradeType: z.union([z.literal(0), z.literal(1)]),
});

/**
 * Body of update-(future-)symbol-groups: the whole group. A new group must send id: "" (the old app does): leaving id
 * out is a 400, because the entity's non-nullable Id is implicitly required. A Mongo id REPLACES the whole stored
 * document, accountId included: an update that does not send back the accountId it loaded saves the group with no
 * account, and from then on it is neither listed nor deletable. Backend item B9 makes the server set it instead.
 */
export const symbolGroupRequest = z
  .object({
    id: z.union([mongoId, z.literal('')]),
    accountId: z.string().nullish(),
    name: z.string().min(1),
    symbols: z.array(z.string().min(1)),
  })
  .refine((group) => group.id === '' || (typeof group.accountId === 'string' && group.accountId.length > 0), {
    message: 'An update must send back the accountId it loaded: the server replaces the whole document.',
    path: ['accountId'],
  });

/** Body of get-line-chart. drop is a percent (the server divides by 100); interval is a KlineInterval in seconds. */
export const lineChartRequest = z.object({
  width: z.int().positive(),
  drop: z.number().positive(),
  symbols: z.array(z.string().min(1)).min(1),
  interval: z.int().positive(),
  closeBased: z.boolean(),
  ignoreSlopeDirection: z.boolean(),
  isTopChart: z.boolean(),
});

export const loginRequest = z.object({
  userName: z.string().min(1),
  password: z.string().min(1),
});

// ---------------------------------------------------------------------------------------------------------------------
// Inferred types

export type GroupTradeLeg = z.infer<typeof groupTradeLeg>;
export type BotTrade = z.infer<typeof botTrade>;
export type SpotGroupTrade = z.infer<typeof spotGroupTrade>;
export type FuturesGroupTrade = z.infer<typeof futuresGroupTrade>;
export type LineTrade = z.infer<typeof lineTrade>;
export type Order = z.infer<typeof order>;
export type SymbolOrders = z.infer<typeof symbolOrders>;
export type OrdersResult = z.infer<typeof ordersResult>;
export type TradeSummary = z.infer<typeof tradeSummary>;
export type CloseTradeStatus = z.infer<typeof closeTradeStatus>;
export type CloseTradeResult = z.infer<typeof closeTradeResult>;
export type CloseGroupResult = z.infer<typeof closeGroupResult>;
export type CancelOrderResult = z.infer<typeof cancelOrderResult>;
export type AddGroupResult = z.infer<typeof addGroupResult>;
export type Features = z.infer<typeof featuresResponse>;
export type PriceSnapshotItem = z.infer<typeof priceSnapshotItem>;
export type CancelOrdersResult = z.infer<typeof cancelOrdersResult>;
export type SpotSymbol = z.infer<typeof spotSymbol>;
export type FuturesSymbol = z.infer<typeof futuresSymbol>;
export type SymbolGroup = z.infer<typeof symbolGroup>;
export type Kline = z.infer<typeof kline>;
export type LineChart = z.infer<typeof lineChart>;
export type PriceTick = z.infer<typeof priceTick>;
export type ProblemDetails = z.infer<typeof problemDetails>;
export type ApiErrorBody = z.infer<typeof apiErrorBody>;

export type SpotGroupTradesResponse = z.infer<typeof spotGroupTradesResponse>;
export type FuturesGroupTradesResponse = z.infer<typeof futuresGroupTradesResponse>;
export type LineTradesResponse = z.infer<typeof lineTradesResponse>;
export type CloseGroupTradeResponse = z.infer<typeof closeSpotGroupTradeResponse>;
export type OrdersResponse = z.infer<typeof spotOrdersResponse>;
export type CancelAllOrdersResponse = z.infer<typeof cancelAllSpotOrdersResponse>;
export type TradeHistoryResponse = z.infer<typeof spotTradeHistoryResponse>;
export type SpotSymbolsResponse = z.infer<typeof spotSymbolsResponse>;
export type FuturesSymbolsResponse = z.infer<typeof futuresSymbolsResponse>;
export type SymbolGroupsResponse = z.infer<typeof spotSymbolGroupsResponse>;
export type KlinesResponse = z.infer<typeof klinesResponse>;
export type LineChartsResponse = z.infer<typeof lineChartsResponse>;
export type LoginResponse = z.infer<typeof loginResponse>;
export type ReceivePriceMessage = z.infer<typeof receivePriceMessage>;
export type ReceiveFuturesPriceMessage = z.infer<typeof receiveFuturesPriceMessage>;

export type TradeHistoryRequest = z.infer<typeof tradeHistoryRequest>;
export type GroupTradeRequest = z.infer<typeof groupTradeRequest>;
export type FuturesGroupTradeRequest = z.infer<typeof futuresGroupTradeRequest>;
export type SymbolGroupRequest = z.infer<typeof symbolGroupRequest>;
export type LineChartRequest = z.infer<typeof lineChartRequest>;
export type LoginRequest = z.infer<typeof loginRequest>;
export type P2PPrices = z.infer<typeof p2pPricesResponse>;
export type ExchangeAccount = z.infer<typeof exchangeAccount>;
export type AccountRequest = z.infer<typeof accountRequest>;
export type P2PCalculateRequest = z.infer<typeof p2pCalculateRequest>;
export type P2PCalculateResponse = z.infer<typeof p2pCalculateResponse>;

// ---------------------------------------------------------------------------------------------------------------------
// Endpoint inventory

export type EndpointSpec = {
  /** HUB: a SignalR hub event rather than an HTTP call. */
  method: 'GET' | 'POST' | 'HUB';
  /** Path under VITE_API_URL; :name marks a route parameter. Query parameters are listed in notes. */
  path: string;
  /** False when calling it can place, cancel or close an order, or write to the database. */
  readOnly: boolean;
  market: 'spot' | 'usdm' | null;
  /** How the success body arrives. */
  body: 'json' | 'json-or-empty' | 'empty' | 'text' | 'signalr';
  /** Name of the exported response schema. */
  schemaName: string;
  schema: z.ZodType;
  /** The request body schema, for POST endpoints that take one. */
  requestSchemaName?: string;
  /** New-app routes that use it (rebuild plan). */
  usedBy: readonly string[];
  notes: string;
};

/**
 * Every endpoint the old app (Trader.Web/trader-new, removed; in the git history) called, keyed by the name fixtures use:
 * src/api/fixtures/<key>.<case>.synthetic.json, <key>.<case>.json, <key>.recorded.json.
 */
export const ENDPOINTS = {
  // Spot groups: GroupTradeController
  spotGroupTrades: {
    method: 'GET',
    path: '/GroupTrade/get-group-trades',
    readOnly: true,
    market: 'spot',
    body: 'json',
    schemaName: 'spotGroupTradesResponse',
    schema: spotGroupTradesResponse,
    usedBy: ['/groups/:market', '/groups/:market/:groupId'],
    notes:
      'Active groups plus groups created today (server date), active first, newest first. Open legs get a live profit; ' +
      'an open leg whose symbol has no row in Symbols makes the whole call a 500.',
  },
  addSpotGroupTrade: {
    method: 'POST',
    path: '/GroupTrade/add-group-trade',
    readOnly: false,
    market: 'spot',
    body: 'json-or-empty',
    schemaName: 'addSpotGroupTradeResponse',
    schema: addSpotGroupTradeResponse,
    requestSchemaName: 'groupTradeRequest',
    usedBy: ['/groups/:market/new'],
    notes:
      'Cleans the symbols (trim, upper-case, split pasted lists, drop repeats), then checks every symbol, the value and ' +
      'the take-profit before the first order: a problem is a 400 with the reason as text, and nothing is ordered. ' +
      'Then one market buy per symbol, the group saved as soon as they are out, then the take-profit sells. The body ' +
      'says per symbol what opened, what did not, what is unconfirmed and what has no take-profit (addGroupResult). ' +
      'No group is saved when nothing opened. An entry whose outcome Binance did not confirm is looked up for about 10 ' +
      'seconds and, if still not found, listed as unconfirmed, never as not opened. A failed save of the take-profit ' +
      'ids is a warning, not an error. Only a failure to save the group itself, after the buys, is still a 500. An API ' +
      'from before 2026-11-02 answers an empty body instead, and could fail with a 500 after buying for many reasons, ' +
      'leaving positions outside any group.',
  },
  closeSpotGroupTrade: {
    method: 'POST',
    path: '/GroupTrade/close-group-trade/:groupId/:buyId',
    readOnly: false,
    market: 'spot',
    body: 'json-or-empty',
    schemaName: 'closeSpotGroupTradeResponse',
    schema: closeSpotGroupTradeResponse,
    usedBy: ['/groups/:market', '/groups/:market/:groupId'],
    notes:
      'buyId 0 closes every leg, otherwise the leg with that buyOrderId. Cancels the take-profit, then market sells; a ' +
      'leg whose sell then failed or is unconfirmed ends its error with "; its take-profit order was cancelled" (since ' +
      '2026-11-02). Unknown groupId: 500. Another close running for 30 s: 409 with a detail that is safe to show, and ' +
      'nothing was sent (a 500 with the same detail before 2026-11-02).',
  },

  // Futures groups: FutureGroupTradeController
  futuresGroupTrades: {
    method: 'GET',
    path: '/FutureGroupTrade/get-future-group-trades',
    readOnly: true,
    market: 'usdm',
    body: 'json',
    schemaName: 'futuresGroupTradesResponse',
    schema: futuresGroupTradesResponse,
    usedBy: ['/groups/:market', '/groups/:market/:groupId'],
    notes: 'As spotGroupTrades, plus tradeType. Open legs get a live profit, computed as a short unless tradeType is 1.',
  },
  addFuturesGroupTrade: {
    method: 'POST',
    path: '/FutureGroupTrade/add-future-group-trade',
    readOnly: false,
    market: 'usdm',
    body: 'json-or-empty',
    schemaName: 'addFuturesGroupTradeResponse',
    schema: addFuturesGroupTradeResponse,
    requestSchemaName: 'futuresGroupTradeRequest',
    usedBy: ['/groups/:market/new'],
    notes:
      'As addSpotGroupTrade, with reduce-only take-profits. tradeType must be 1 (long) or 0 (short): anything else, or ' +
      'none, is a 400 before any order, and a short needs a take-profit below 100%. Each entry\'s fill is read back, ' +
      'so legs get the filled quantity and average price; only when that read fails three times is a leg kept with ' +
      'quantity 0, which the close path recovers from the entry order (an API from before 2026-11-02 always kept ' +
      'quantity 0 and the kline price on groups without a take-profit).',
  },
  closeFuturesGroupTrade: {
    method: 'POST',
    path: '/FutureGroupTrade/close-future-group-trade/:groupId/:buyId',
    readOnly: false,
    market: 'usdm',
    body: 'json-or-empty',
    schemaName: 'closeFuturesGroupTradeResponse',
    schema: closeFuturesGroupTradeResponse,
    usedBy: ['/groups/:market', '/groups/:market/:groupId'],
    notes:
      'As closeSpotGroupTrade, with reduce-only market orders. Since 2026-11-02 a leg gets a closeClientID before its ' +
      'close order goes out, as on spot, and the next close looks that order up before sending another one.',
  },

  // Line-break groups: LineBreakController (decision D2)
  lineTrades: {
    method: 'GET',
    path: '/LineBreak/get-line-trades',
    readOnly: false,
    market: 'spot',
    body: 'json',
    schemaName: 'lineTradesResponse',
    schema: lineTradesResponse,
    usedBy: ['/line-trades'],
    notes:
      'NOT read-only although it is a GET: when Binance reports every leg of a group filled it marks the legs finished ' +
      'and saves the group as inactive, with the profits it just computed. Active groups only, newest first. One Binance ' +
      'order lookup per leg.',
  },
  addLineTrade: {
    method: 'POST',
    path: '/LineBreak/add-line-trade',
    readOnly: false,
    market: 'spot',
    body: 'json-or-empty',
    schemaName: 'addLineTradeResponse',
    schema: addLineTradeResponse,
    requestSchemaName: 'groupTradeRequest',
    usedBy: ['/line-trades'],
    notes:
      'As addSpotGroupTrade, for line-break groups.',
  },
  closeLineTrade: {
    method: 'POST',
    path: '/LineBreak/close-line-trade/:lineId/:buyId',
    readOnly: false,
    market: 'spot',
    body: 'json-or-empty',
    schemaName: 'closeLineTradeResponse',
    schema: closeLineTradeResponse,
    usedBy: ['/line-trades'],
    notes: 'As closeSpotGroupTrade, except that an error never says the take-profit was cancelled.',
  },
  lineCharts: {
    method: 'POST',
    path: '/LineBreak/get-line-chart',
    readOnly: true,
    market: 'spot',
    body: 'json',
    schemaName: 'lineChartsResponse',
    schema: lineChartsResponse,
    requestSchemaName: 'lineChartRequest',
    usedBy: ['/charts/lines'],
    notes:
      'POST but read-only. Loads 96 h of spot klines per symbol and renders one PNG per line found with Plotly and a ' +
      'headless browser on the server: slow and large (hundreds of KB per image). Sorted by symbol.',
  },

  // Symbols: SymbolsController, FutureSymbolsController
  spotSymbols: {
    method: 'GET',
    path: '/Symbols/get-symbols',
    readOnly: true,
    market: 'spot',
    body: 'json',
    schemaName: 'spotSymbolsResponse',
    schema: spotSymbolsResponse,
    usedBy: ['/symbols/:market'],
    notes:
      'Every stored USDT symbol with live price, volume and change: one Binance kline call per symbol (about 2 request ' +
      'weight each). Sorted by volume; /Symbols/get-symbols/lose sorts by change instead.',
  },
  spotSymbolsBasic: {
    method: 'GET',
    path: '/Symbols/get-symbols-basic',
    readOnly: true,
    market: 'spot',
    body: 'json',
    schemaName: 'spotSymbolsBasicResponse',
    schema: spotSymbolsBasicResponse,
    usedBy: ['/charts/lines'],
    notes: 'Same shape, no Binance call: price, volume and change are what the last symbol sync stored.',
  },
  updateSpotSymbols: {
    method: 'POST',
    path: '/Symbols/update-symbols',
    readOnly: false,
    market: 'spot',
    body: 'empty',
    schemaName: 'updateSpotSymbolsResponse',
    schema: updateSpotSymbolsResponse,
    usedBy: ['/symbols/:market'],
    notes: 'Sync: adds new USDT symbols from Binance and rewrites precision, price, volume and change of the others.',
  },
  spotSymbolGroups: {
    method: 'GET',
    path: '/Symbols/get-symbol-groups',
    readOnly: true,
    market: 'spot',
    body: 'json',
    schemaName: 'spotSymbolGroupsResponse',
    schema: spotSymbolGroupsResponse,
    usedBy: ['/symbols/:market', '/groups/:market/new', '/charts/klines', '/charts/lines', '/line-trades'],
    notes: "The account's spot symbol groups, in storage order.",
  },
  saveSpotSymbolGroup: {
    method: 'POST',
    path: '/Symbols/update-symbol-groups',
    readOnly: false,
    market: 'spot',
    body: 'empty',
    schemaName: 'saveSpotSymbolGroupResponse',
    schema: saveSpotSymbolGroupResponse,
    requestSchemaName: 'symbolGroupRequest',
    usedBy: ['/symbols/:market'],
    notes:
      'Creates the group when id is "", replaces the whole stored document when id is set (send back the loaded accountId, ' +
      'see symbolGroupRequest); no id at all is a 400. The old app does not await the spot call.',
  },
  deleteSpotSymbolGroup: {
    method: 'POST',
    path: '/Symbols/delete-symbol-groups/:id',
    readOnly: false,
    market: 'spot',
    body: 'empty',
    schemaName: 'deleteSpotSymbolGroupResponse',
    schema: deleteSpotSymbolGroupResponse,
    usedBy: ['/symbols/:market'],
    notes: 'Ok() whether or not the id existed.',
  },
  futuresSymbols: {
    method: 'GET',
    path: '/FutureSymbols/get-future-symbols',
    readOnly: true,
    market: 'usdm',
    body: 'json',
    schemaName: 'futuresSymbolsResponse',
    schema: futuresSymbolsResponse,
    usedBy: ['/', '/symbols/:market'],
    notes:
      'Query ?symbols=A,B limits it (the dashboard does). Without it, one futures kline call per stored symbol at ' +
      'about 5 request weight each. No change field. Sorted by volume.',
  },
  futuresSymbolsBasic: {
    method: 'GET',
    path: '/FutureSymbols/get-future-symbols-basic',
    readOnly: true,
    market: 'usdm',
    body: 'json',
    schemaName: 'futuresSymbolsResponse',
    schema: futuresSymbolsResponse,
    usedBy: ['/symbols/:market'],
    notes: 'As spotSymbolsBasic: no Binance call, price and volume as the last sync stored them. Reported by /Features (futuresSymbolsBasic).',
  },
  accounts: {
    method: 'GET',
    path: '/Accounts',
    readOnly: true,
    market: null,
    body: 'json',
    schemaName: 'accountsResponse',
    schema: accountsResponse,
    usedBy: ['/accounts'],
    notes: "The signed-in user's accounts, the current one marked. Never the keys.",
  },
  addAccount: {
    method: 'POST',
    path: '/Accounts',
    readOnly: false,
    market: null,
    body: 'json',
    schemaName: 'exchangeAccount',
    schema: exchangeAccount,
    requestSchemaName: 'accountRequest',
    usedBy: ['/accounts'],
    notes: 'Checks the keys with the exchange first (Binance: account info; OKX: account configuration), then stores them encrypted. 400 problem details with the reason.',
  },
  updateAccount: {
    // POST, not PUT: IIS's WebDAV module answers PUT and DELETE with 405 before they reach the API.
    method: 'POST',
    path: '/Accounts/:id',
    readOnly: false,
    market: null,
    body: 'json',
    schemaName: 'exchangeAccount',
    schema: exchangeAccount,
    requestSchemaName: 'accountRequest',
    usedBy: ['/accounts'],
    notes: 'Renames, or replaces the keys (checked first). Empty keys keep the current ones.',
  },
  removeAccount: {
    method: 'POST',
    path: '/Accounts/:id/remove',
    readOnly: false,
    market: null,
    body: 'empty',
    schemaName: 'emptyBody',
    schema: emptyBody,
    usedBy: ['/accounts'],
    notes: 'Refused (400) for the account in use, the only account, and one with active group or line trades.',
  },
  switchAccount: {
    method: 'POST',
    path: '/Accounts/:id/switch',
    readOnly: true,
    market: null,
    body: 'text',
    schemaName: 'loginResponse',
    schema: loginResponse,
    usedBy: ['/accounts', 'the account switcher'],
    notes: 'A new token ("Bearer <jwt>") for that account; the next sign-in opens it too.',
  },
  p2pPrices: {
    method: 'GET',
    path: '/P2P/prices',
    readOnly: true,
    market: null,
    body: 'json',
    schemaName: 'p2pPricesResponse',
    schema: p2pPricesResponse,
    usedBy: ['/p2p'],
    notes: "Reads Binance P2P's public search (no official API), best offer first, kept 30 s on the server. 502 problem details when Binance cannot be read.",
  },
  p2pCalculate: {
    method: 'POST',
    path: '/P2P/Calculate',
    readOnly: true,
    market: null,
    body: 'json',
    schemaName: 'p2pCalculateResponse',
    schema: p2pCalculateResponse,
    requestSchemaName: 'p2pCalculateRequest',
    usedBy: ['/p2p'],
    notes: 'POST but read-only: arithmetic only. 400 when neither amount nor jodAmount is sent.',
  },
  updateFuturesSymbols: {
    method: 'POST',
    path: '/FutureSymbols/update-future-symbols',
    readOnly: false,
    market: 'usdm',
    body: 'empty',
    schemaName: 'updateFuturesSymbolsResponse',
    schema: updateFuturesSymbolsResponse,
    usedBy: ['/symbols/:market'],
    notes: 'Sync, as updateSpotSymbols.',
  },
  futuresSymbolGroups: {
    method: 'GET',
    path: '/FutureSymbols/get-future-symbol-groups',
    readOnly: true,
    market: 'usdm',
    body: 'json',
    schemaName: 'futuresSymbolGroupsResponse',
    schema: futuresSymbolGroupsResponse,
    usedBy: ['/symbols/:market', '/groups/:market/new'],
    notes: "The account's futures symbol groups.",
  },
  saveFuturesSymbolGroup: {
    method: 'POST',
    path: '/FutureSymbols/update-future-symbol-groups',
    readOnly: false,
    market: 'usdm',
    body: 'empty',
    schemaName: 'saveFuturesSymbolGroupResponse',
    schema: saveFuturesSymbolGroupResponse,
    requestSchemaName: 'symbolGroupRequest',
    usedBy: ['/symbols/:market'],
    notes: 'As saveSpotSymbolGroup.',
  },
  deleteFuturesSymbolGroup: {
    method: 'POST',
    path: '/FutureSymbols/delete-future-symbol-groups/:id',
    readOnly: false,
    market: 'usdm',
    body: 'empty',
    schemaName: 'deleteFuturesSymbolGroupResponse',
    schema: deleteFuturesSymbolGroupResponse,
    usedBy: ['/symbols/:market'],
    notes: 'As deleteSpotSymbolGroup.',
  },

  // Bot: TradesController, FutureTradesController (decision D1)
  spotOrders: {
    method: 'GET',
    path: '/Trades/get-orders',
    readOnly: true,
    market: 'spot',
    body: 'json-or-empty',
    schemaName: 'spotOrdersResponse',
    schema: spotOrdersResponse,
    usedBy: ['/bot/:market/orders', '/bot/:market/orders/:symbol'],
    notes:
      'Query ?symbol=. Empty body when there is no open order at all; zeros and empty lists when there are open orders ' +
      'but none is a bot take-profit. Only sell orders that match a bot trade (Trades collection) are listed: group ' +
      'take-profits are not. One Binance price call per order.',
  },
  futuresOrders: {
    method: 'GET',
    path: '/FutureTrades/get-orders',
    readOnly: true,
    market: 'usdm',
    body: 'json-or-empty',
    schemaName: 'futuresOrdersResponse',
    schema: futuresOrdersResponse,
    usedBy: ['/', '/bot/:market/orders', '/bot/:market/orders/:symbol'],
    notes: 'As spotOrders. Sell orders only, so the take-profits of futures shorts never appear.',
  },
  cancelSpotOrder: {
    method: 'GET',
    path: '/Trades/cancel-order',
    readOnly: false,
    market: 'spot',
    body: 'empty',
    schemaName: 'cancelSpotOrderResponse',
    schema: cancelSpotOrderResponse,
    usedBy: ['/bot/:market/orders/:symbol'],
    notes:
      'A GET that CANCELS an order: ?symbol=&orderId=. Ok() with no body; a Binance refusal is a 400 with its message ' +
      'as text/plain (or an empty body).',
  },
  cancelFuturesOrder: {
    method: 'GET',
    path: '/FutureTrades/cancel-order',
    readOnly: false,
    market: 'usdm',
    body: 'empty',
    schemaName: 'cancelFuturesOrderResponse',
    schema: cancelFuturesOrderResponse,
    usedBy: ['/bot/:market/orders/:symbol'],
    notes: 'A GET that CANCELS an order, as cancelSpotOrder.',
  },
  cancelAllSpotOrders: {
    method: 'GET',
    path: '/Trades/cancel-all-orders',
    readOnly: false,
    market: 'spot',
    body: 'json-or-empty',
    schemaName: 'cancelAllSpotOrdersResponse',
    schema: cancelAllSpotOrdersResponse,
    usedBy: ['/bot/:market/orders'],
    notes:
      'A GET that CANCELS every open order of ?symbol=, not only the bot take-profits the orders screen shows: group ' +
      'take-profits and manual orders on that symbol go too. No or empty symbol: a 400 validation problem ' +
      '(application/problem+json, errors.symbol) from model binding, before the action runs. A Binance error listing ' +
      'the open orders: 400 with Binance\'s message as text. In both cases nothing was cancelled.',
  },
  cancelAllFuturesOrders: {
    method: 'GET',
    path: '/FutureTrades/cancel-all-orders',
    readOnly: false,
    market: 'usdm',
    body: 'json-or-empty',
    schemaName: 'cancelAllFuturesOrdersResponse',
    schema: cancelAllFuturesOrdersResponse,
    usedBy: ['/bot/:market/orders'],
    notes: 'A GET that CANCELS every open order of ?symbol=, as cancelAllSpotOrders.',
  },
  spotTradeHistory: {
    method: 'POST',
    path: '/Trades/get-trades',
    readOnly: true,
    market: 'spot',
    body: 'json',
    schemaName: 'spotTradeHistoryResponse',
    schema: spotTradeHistoryResponse,
    requestSchemaName: 'tradeHistoryRequest',
    usedBy: ['/bot/:market/history'],
    notes: 'POST but read-only. Bot trades with botType null or 0.',
  },
  futuresTradeHistory: {
    method: 'POST',
    path: '/FutureTrades/get-trades',
    readOnly: true,
    market: 'usdm',
    body: 'json',
    schemaName: 'futuresTradeHistoryResponse',
    schema: futuresTradeHistoryResponse,
    requestSchemaName: 'tradeHistoryRequest',
    usedBy: ['/', '/bot/:market/history'],
    notes: 'POST but read-only. Bot trades with botType 1. The dashboard month summary uses it.',
  },

  // Charts: DataController
  klines: {
    method: 'GET',
    path: '/Data/load-data/:symbol',
    readOnly: true,
    market: 'spot',
    body: 'json',
    schemaName: 'klinesResponse',
    schema: klinesResponse,
    usedBy: ['/charts/klines'],
    notes: 'Spot hourly klines of the last 5 days, current candle included. Spot only, even for a futures symbol.',
  },

  // Auth: AccountController
  login: {
    method: 'POST',
    path: '/account/login',
    readOnly: false,
    market: null,
    body: 'text',
    schemaName: 'loginResponse',
    schema: loginResponse,
    requestSchemaName: 'loginRequest',
    usedBy: ['/login'],
    notes:
      'Called with fetch, not the wrapper. 200 text/plain "Bearer <jwt>" (96 h), sent back verbatim as the ' +
      'Authorization header; 401 text/plain "Invalid Login".',
  },

  // Capabilities and prices (phase 1)
  features: {
    method: 'GET',
    path: '/Features',
    readOnly: true,
    market: null,
    body: 'json',
    schemaName: 'featuresResponse',
    schema: featuresResponse,
    usedBy: ['/'],
    notes:
      'Anonymous. What the API supports: per-trade close results, per-symbol add results, the price snapshot, a futures ' +
      'price feed (ReceiveFuturesPrice and the usdm snapshot). An API from before phase 1 answers 404: treat every ' +
      'feature as off.',
  },
  priceSnapshot: {
    method: 'GET',
    path: '/Prices/snapshot',
    readOnly: true,
    market: null,
    body: 'json',
    schemaName: 'priceSnapshotResponse',
    schema: priceSnapshotResponse,
    usedBy: ['/', '/groups/:market', '/groups/:market/:groupId'],
    notes:
      '?market=spot (the default) or ?market=usdm (when /Features says futuresPriceFeed): the last price of every symbol ' +
      'of that market received since the API started, with receivedAt (UTC, server clock) and eventTime (Unix ms, ' +
      "Binance's clock; absent from an older API), sorted by symbol; empty right after a restart. A newest eventTime " +
      "far behind now means the server's own stream has stalled. Any other market is a 400 problem, and so is usdm on an " +
      'API from before the futures feed.',
  },

  // SignalR: Hubs/PriceHub.cs, fed by Subscribers/SymbolPriceStateStore.cs (spot and USD-M futures)
  receivePrice: {
    method: 'HUB',
    path: '/priceHub',
    readOnly: true,
    market: 'spot',
    body: 'signalr',
    schemaName: 'receivePriceMessage',
    schema: receivePriceMessage,
    usedBy: ['/groups/:market', '/groups/:market/:groupId', '/bot/:market/orders', '/bot/:market/orders/:symbol'],
    notes:
      'Event ReceivePrice, about once a second: an array of {symbol, lastPrice, eventTime} for every spot symbol that ' +
      'ticked, any quote asset (eventTime is Unix ms, absent from an older API). Sent to every client. The hub needs no ' +
      'token.',
  },
  receiveFuturesPrice: {
    method: 'HUB',
    path: '/priceHub',
    readOnly: true,
    market: 'usdm',
    body: 'signalr',
    schemaName: 'receiveFuturesPriceMessage',
    schema: receiveFuturesPriceMessage,
    usedBy: ['/groups/:market', '/groups/:market/:groupId', '/bot/:market/orders', '/bot/:market/orders/:symbol'],
    notes:
      'Event ReceiveFuturesPrice, about once a second: the same {symbol, lastPrice, eventTime} items as ReceivePrice, for ' +
      'every USD-M futures symbol that ticked. Sent only to clients that connect to /priceHub?feeds=usdm (the server ' +
      'adds them to its futures group on every connect); the old app does not ask, so it never gets them. Only when ' +
      '/Features says futuresPriceFeed.',
  },
} as const satisfies Record<string, EndpointSpec>;

export type EndpointKey = keyof typeof ENDPOINTS;

/** The parsed success body of an endpoint. */
export type ResponseOf<K extends EndpointKey> = z.infer<(typeof ENDPOINTS)[K]['schema']>;
